import React from 'react';
import { Wrench, Truck, LifeBuoy, ArrowRight, ChevronDown, Droplets, Download } from 'lucide-react';

const capabilities = [
  {
    icon: Wrench,
    title: 'Engineered to Specification',
    blurb:
      'Pump systems sized, specified and engineered to the operating duty, fluid characteristics and site constraints of each industrial application.',
  },
  {
    icon: Truck,
    title: 'Supplied & Installed',
    blurb:
      'End-to-end project execution — equipment supply, mechanical and electrical installation, commissioning and handover.',
  },
  {
    icon: LifeBuoy,
    title: 'Supported End to End',
    blurb:
      'Service, spares and aftermarket support that keeps installed equipment running reliably across its operating life.',
  },
];

const IndustrialPumps = () => {
  const scrollToCapabilities = () => {
    document.getElementById('pumps-capabilities')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      {/* Hero */}
      <section
        id="home"
        className="relative min-h-[80vh] flex items-center overflow-hidden bg-gradient-to-br from-[#1a1a1a] via-[#252525] to-[#2d2d2d]"
      >
        <div className="absolute inset-0 overflow-hidden opacity-25 pointer-events-none">
          <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="pumps-grid" width="48" height="48" patternUnits="userSpaceOnUse">
                <path d="M 48 0 L 0 0 0 48" fill="none" stroke="rgba(62,181,160,0.18)" strokeWidth="0.6" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#pumps-grid)" />
          </svg>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 pt-32 md:pt-36 w-full">
          <div className="max-w-4xl">
            <p
              className="animate-hero-rise inline-flex items-center gap-2 text-xs md:text-sm uppercase tracking-[0.3em] text-[#3eb5a0] font-medium mb-6 md:mb-8 border border-[#3eb5a0]/50 rounded-full px-4 py-1.5"
              style={{ animationDelay: '0ms' }}
            >
              <Droplets size={14} strokeWidth={2.2} />
              Reshadi Industrial · Pumps
            </p>
            <h1
              className="animate-hero-rise text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.15] tracking-tight mb-6 md:mb-8"
              style={{ animationDelay: '120ms' }}
            >
              Turnkey industrial
              <br />
              <span className="text-[#3eb5a0]">pumping solutions.</span>
            </h1>
            <p
              className="animate-hero-rise text-lg md:text-xl text-white/80 font-light max-w-3xl mb-8 md:mb-10"
              style={{ animationDelay: '240ms', lineHeight: '1.7' }}
            >
              Engineered, supplied, installed and supported — complete industrial pumping
              solutions across water, slurry, process and dewatering duties.
            </p>
            <div
              className="animate-hero-rise flex flex-col sm:flex-row gap-4"
              style={{ animationDelay: '360ms' }}
            >
              <button
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="pumps-engage-btn"
                className="group inline-flex items-center justify-center gap-3 px-7 md:px-9 py-3.5 md:py-4 bg-[#3eb5a0] text-black font-semibold rounded-full shadow-lg hover:bg-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-[#3eb5a0] focus:ring-offset-2 focus:ring-offset-[#1a1a1a]"
              >
                <span>Enquire about pumps</span>
                <ArrowRight
                  size={18}
                  strokeWidth={2.4}
                  className="transition-transform duration-300 group-hover:translate-x-0.5"
                />
              </button>

              <a
                href="https://customer-assets-lqy194kg.emergentagent.net/job_reshadi-replica/artifacts/nq10gvzu_Reshadi%20company%20pump%20profile.pdf"
                target="_blank"
                rel="noopener noreferrer"
                download="Reshadi_Pump_Profile.pdf"
                data-testid="pumps-download-btn"
                className="group inline-flex items-center justify-center gap-3 px-7 md:px-9 py-3.5 md:py-4 bg-transparent border-2 border-[#3eb5a0] text-[#3eb5a0] font-semibold rounded-full hover:bg-[#3eb5a0] hover:text-black transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#3eb5a0] focus:ring-offset-2 focus:ring-offset-[#1a1a1a]"
              >
                <Download size={18} strokeWidth={2.2} />
                <span>Download pump profile</span>
              </a>
            </div>
          </div>
        </div>

        <button
          onClick={scrollToCapabilities}
          aria-label="Scroll to capabilities"
          className="absolute bottom-6 md:bottom-8 left-6 md:left-12 w-12 h-12 md:w-14 md:h-14 rounded-full border-2 border-[#3eb5a0] bg-transparent text-[#3eb5a0] flex items-center justify-center hover:bg-[#3eb5a0] hover:text-black transition-all duration-300"
        >
          <ChevronDown size={22} />
        </button>
      </section>

      {/* Capabilities */}
      <section
        id="pumps-capabilities"
        className="relative py-16 md:py-24 lg:py-28 bg-[#1a1a1a]"
        data-testid="pumps-capabilities"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-10 md:mb-14 max-w-3xl">
            <p className="text-xs uppercase tracking-[0.3em] text-[#3eb5a0] font-semibold mb-3">
              Capabilities
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
              A single point of accountability across the
              {' '}
              <span className="text-[#3eb5a0]">full pumping lifecycle.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5 md:gap-6 mb-12 md:mb-14">
            {capabilities.map((c, i) => {
              const Icon = c.icon;
              return (
                <article
                  key={c.title}
                  data-testid={`pumps-cap-${i}`}
                  className="group relative overflow-hidden rounded-2xl bg-[#252525] border border-[#3eb5a0]/15 p-6 md:p-8 transition-all duration-300 hover:border-[#3eb5a0] hover:-translate-y-1 hover:shadow-2xl"
                >
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-[#3eb5a0]/10 border border-[#3eb5a0]/30 flex items-center justify-center mb-5 md:mb-6 transition-colors duration-300 group-hover:bg-[#3eb5a0]">
                    <Icon size={22} className="text-[#3eb5a0] transition-colors duration-300 group-hover:text-black" strokeWidth={2} />
                  </div>
                  <h3 className="text-xl md:text-2xl font-semibold text-white mb-3">{c.title}</h3>
                  <p className="text-sm md:text-base text-white/65 font-light" style={{ lineHeight: '1.75' }}>
                    {c.blurb}
                  </p>
                </article>
              );
            })}
          </div>

          <div className="border-l-2 border-[#3eb5a0] pl-5 md:pl-6 max-w-3xl">
            <p className="text-base md:text-lg text-white/70 font-light italic mb-5" style={{ lineHeight: '1.85' }}>
              Detailed pump models, brands and datasheets are available on request. Get in touch
              with a short description of your duty and site conditions and we&apos;ll come back
              with a fitted proposal.
            </p>
            <a
              href="https://customer-assets-lqy194kg.emergentagent.net/job_reshadi-replica/artifacts/nq10gvzu_Reshadi%20company%20pump%20profile.pdf"
              target="_blank"
              rel="noopener noreferrer"
              download="Reshadi_Pump_Profile.pdf"
              data-testid="pumps-download-btn-secondary"
              className="group inline-flex items-center gap-3 px-6 md:px-7 py-3 md:py-3.5 bg-[#3eb5a0] text-black font-semibold rounded-full shadow-lg hover:bg-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-[#3eb5a0] focus:ring-offset-2 focus:ring-offset-[#1a1a1a]"
            >
              <Download size={18} strokeWidth={2.2} />
              <span>Download the Reshadi Pump Profile</span>
            </a>
            <p className="text-xs md:text-sm text-white/50 font-light mt-2">PDF · opens in a new tab</p>
          </div>
        </div>
      </section>
    </>
  );
};

export default IndustrialPumps;
